import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatErr } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { getAffiliateCode } from "../components/AffiliateTracker";

export default function Checkout() {
    const { items, subtotal } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [coupon, setCoupon] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [useWallet, setUseWallet] = useState(false);
    const [addr, setAddr] = useState({
        full_name: user?.name || "",
        street: "",
        city: "",
        postal_code: "",
        country: "Portugal",
        phone: "",
    });

    useEffect(() => {
        if (user?.role === "customer") {
            api.get("/customer/wallet").then((r) => setWalletBalance(r.data.balance || 0)).catch(() => {});
        }
    }, [user]);

    if (items.length === 0) {
        return (
            <>
                <Navbar variant="light" />
                <div className="pt-40 container-edge text-wine-dark/60 text-center">
                    Carrinho vazio.{" "}
                    <button onClick={() => navigate("/marketplace")} className="underline">Voltar ao marketplace</button>
                </div>
            </>
        );
    }

    async function applyCoupon() {
        try {
            const { data } = await api.get(`/coupons/validate/${coupon}`);
            setAppliedCoupon(data);
            toast.success(`Cupão ${data.code} aplicado: -${data.discount_pct}%`);
        } catch (e) {
            toast.error("Cupão inválido");
            setAppliedCoupon(null);
        }
    }

    const discount = appliedCoupon ? subtotal * (appliedCoupon.discount_pct / 100) : 0;
    const shipping = subtotal >= 50 ? 0 : 4.9;
    const walletApplied = useWallet ? Math.min(walletBalance, Math.max(subtotal - discount, 0)) : 0;
    const total = Math.max(subtotal - discount - walletApplied + shipping, 0);

    async function pay(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/orders/checkout", {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                shipping_address: addr,
                coupon_code: appliedCoupon?.code,
                use_wallet: useWallet,
                affiliate_code: getAffiliateCode(),
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
            setBusy(false);
        }
    }

    return (
        <>
            <Navbar variant="light" />
            <section className="pt-32 pb-20 bg-beige-natural">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <form onSubmit={pay} className="lg:col-span-7 space-y-8" data-testid="checkout-form">
                        <div>
                            <span className="label-overline text-wine">Checkout</span>
                            <h1 className="mt-4 font-heading text-4xl md:text-5xl text-wine-dark">Endereço de envio</h1>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="Nome completo" value={addr.full_name} set={(v) => setAddr({ ...addr, full_name: v })} testId="addr-name" />
                            <Field label="Telefone" value={addr.phone} set={(v) => setAddr({ ...addr, phone: v })} testId="addr-phone" />
                            <Field label="Morada" value={addr.street} set={(v) => setAddr({ ...addr, street: v })} testId="addr-street" full />
                            <Field label="Cidade" value={addr.city} set={(v) => setAddr({ ...addr, city: v })} testId="addr-city" />
                            <Field label="Código postal" value={addr.postal_code} set={(v) => setAddr({ ...addr, postal_code: v })} testId="addr-postal" />
                        </div>

                        <div className="bg-white p-6 border border-wine-dark/10">
                            <span className="label-overline text-wine">Cupão</span>
                            <div className="mt-3 flex gap-2">
                                <input
                                    value={coupon}
                                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                    placeholder="BEMVINDO10"
                                    className="flex-1 bg-transparent border border-wine-dark/20 px-4 py-3 text-sm focus:border-wine"
                                    data-testid="coupon-input"
                                />
                                <button type="button" onClick={applyCoupon} className="btn-wine-outline" data-testid="coupon-apply">
                                    Aplicar
                                </button>
                            </div>
                        </div>

                        <button disabled={busy} className="btn-wine w-full md:w-auto" data-testid="checkout-pay">
                            {busy ? "A redirecionar…" : `Pagar €${total.toFixed(2)} com Stripe`}
                        </button>
                        <p className="text-xs text-wine-dark/60">
                            Pagamento seguro processado via Stripe. Suportamos cartão de crédito e
                            débito.
                        </p>
                    </form>

                    <aside className="lg:col-span-5">
                        <div className="bg-white p-8 border border-wine-dark/10 sticky top-28">
                            <h3 className="label-overline text-wine">A sua encomenda</h3>
                            <div className="mt-6 space-y-4">
                                {items.map((it) => (
                                    <div key={it.product_id} className="flex items-center gap-4">
                                        <img src={it.image} alt={it.name} className="w-14 h-14 object-cover" />
                                        <div className="flex-1">
                                            <p className="text-sm text-wine-dark">{it.name}</p>
                                            <p className="text-xs text-wine-dark/60">{it.quantity} × €{it.price.toFixed(2)}</p>
                                        </div>
                                        <span className="text-sm">€{(it.quantity * it.price).toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="hairline my-6" />
                            <div className="space-y-2 text-sm text-wine-dark/80">
                                <Row label="Subtotal" value={`€${subtotal.toFixed(2)}`} />
                                {appliedCoupon && <Row label={`Cupão ${appliedCoupon.code}`} value={`-€${discount.toFixed(2)}`} />}
                                {walletApplied > 0 && <Row label="Crédito Caseirinho" value={`-€${walletApplied.toFixed(2)}`} />}
                                <Row label="Envio" value={shipping === 0 ? "Grátis" : `€${shipping.toFixed(2)}`} />
                            </div>
                            {user?.role === "customer" && walletBalance > 0 && (
                                <label className="mt-4 flex items-center gap-3 bg-vineyard/5 border border-vineyard/25 rounded-md px-4 py-3 cursor-pointer" data-testid="use-wallet-toggle">
                                    <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="accent-vineyard h-4 w-4" data-testid="use-wallet-checkbox" />
                                    <span className="flex items-center gap-2 text-sm text-wine-dark">
                                        <Wallet className="h-4 w-4 text-vineyard" />
                                        Usar crédito Caseirinho <span className="font-heading text-vineyard">€{walletBalance.toFixed(2)}</span>
                                    </span>
                                </label>
                            )}
                            {useWallet && walletApplied > 0 && (
                                <p className="text-[11px] text-wine-dark/50 mt-1.5 ml-1">Desconto aplicado até ao valor dos produtos (não cobre portes).</p>
                            )}
                            <div className="hairline my-6" />
                            <div className="flex items-center justify-between">
                                <span className="label-overline text-wine">Total</span>
                                <span className="font-heading text-3xl text-wine-dark">€{total.toFixed(2)}</span>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
            <Footer />
        </>
    );
}

function Field({ label, value, set, testId, full }) {
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="label-overline text-wine-dark">{label}</span>
            <input
                value={value}
                onChange={(e) => set(e.target.value)}
                required
                className="mt-2 w-full bg-transparent border border-wine-dark/20 px-4 py-3 text-sm focus:border-wine"
                data-testid={testId}
            />
        </label>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between">
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}
